/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";
import { strings } from "../../../../common/Strings/Strings";
import { getGeneralError } from "../../../../util/helperFunctions";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import BasicSettings from "../../Profile/settings/BasicSettings";
import Contacts from "../../Profile/settings/Contacts";
import Employees from "../../Profile/settings/Employees";
import ReservationTypes from "../../Profile/settings/ReservationTypes";
import OpeningHours from "../../Profile/settings/OpeningHours";
import SpecialOpeningHours from "../../Profile/settings/SpecialOpeningHours";
import CustomSections from "../../Profile/settings/CustomSections";
import AlertBox from "../../../../components/AlertBox";
import LoaderInline from "../../../../components/LoaderInline";
import { Link } from "react-router-dom";
import Button from "../../../../components/Button";
import { DocumentDuplicate } from "../../../../common/Icons/DocumentDuplicate";
import Params from "../../../../common/Params";

interface Props {
  clinic: ClinicResponse;
  isActive: boolean;
}

const PublicProfileTab: React.FC<Props> = (props: Props) => {
  const { clinic, isActive } = props;
  const [settings, setSettings] = useState<ClinicProfileSettingsResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const clinicId = clinic.id;

  useEffect(() => {
    const getProfileSettings = async (id: string) => {
      try {
        setLoading(true);
        const response = await ClinicProfileApi.getClinicProfileSettings(id);
        setSettings(response.data);
      } catch (e) {
        setError(await getGeneralError(e));
      } finally {
        setLoading(false);
      }
    };

    if (isActive) {
      void getProfileSettings(clinicId);
    }
  }, [clinicId, isActive]);

  if (loading) {
    return <LoaderInline />;
  }

  if (!settings && error) {
    return <AlertBox message={error} />;
  }

  if (!settings) {
    return <></>;
  }

  return (
    <div className="w-full md:tw-card md:max-w-2xl space-y-6 md:p-8">
      <div className="flex">
        <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white flex-1">
          {strings.clinicPublicProfile}
        </h2>
        <div>
          <Link to={`/clinics/${clinicId}`}>{strings.viewClinicProfile}</Link>
        </div>
      </div>
      <div>
        <Button
          onClick={() => {
            navigator.clipboard.writeText(`${Params.frontendBaseURL}/clinics/${clinic.id}`);
          }}
          className="tw-link-dark"
          variant="link"
        >
          <div className="flex">
            <DocumentDuplicate />
            <span className="ml-2">{strings.copyLink}</span>
          </div>
        </Button>
      </div>
      <div>{strings.clinicProfileSettingsTooltip}</div>
      <BasicSettings clinic={clinic} settings={settings} setSettings={setSettings} />
      <Contacts clinic={clinic} settings={settings} setSettings={setSettings} />
      <Employees clinic={clinic} settings={settings} setSettings={setSettings} />
      <ReservationTypes clinic={clinic} settings={settings} setSettings={setSettings} />
      <OpeningHours clinic={clinic} settings={settings} setSettings={setSettings} />
      <SpecialOpeningHours clinic={clinic} settings={settings} setSettings={setSettings} />
      <CustomSections clinic={clinic} settings={settings} setSettings={setSettings} />
    </div>
  );
};

export default PublicProfileTab;
